/**
 * Dev-harness preview of the onboarding rail.
 *
 * The harness writes a beat into sessionStorage (shared with the preview
 * iframe). Only that iframe honors it, so opening the real dashboard in this
 * tab does not lock the shell. Preview never PATCHes onboarding progress.
 */
import useUser from '@/composables/useUser'
import { canAccessDevHarness } from '@/utils/env'

const PREVIEW_BEAT_KEY = 'lighthouse_dev_onboarding_preview'

function isPreviewFrame(): boolean {
    try {
        return window.self !== window.top
    } catch {
        return false
    }
}

export function readOnboardingPreviewBeat(): string | null {
    try {
        const value = sessionStorage.getItem(PREVIEW_BEAT_KEY)
        return value && value.trim() ? value : null
    } catch {
        return null
    }
}

/** Parent harness page. The iframe reads the same key. */
export function setOnboardingPreviewBeat(beat: string | null): void {
    try {
        if (beat) sessionStorage.setItem(PREVIEW_BEAT_KEY, beat)
        else sessionStorage.removeItem(PREVIEW_BEAT_KEY)
    } catch {
        // Private-mode storage failures just skip the preview jump.
    }
}

/** True inside the harness iframe when a beat is selected and the user may use the harness. */
export function isOnboardingPreview(): boolean {
    if (!isPreviewFrame() || !readOnboardingPreviewBeat()) return false
    const email = useUser().user.value?.email as string | undefined
    return canAccessDevHarness(email)
}
